import { ActionTree, GetterTree, Module, MutationTree } from 'vuex';
import { Document, FollowUp, GrievanceItemState, RootState } from '@/store/types';
import axios from '@/utils/axios';
import moment from 'moment';
import i18n from '@/i18n';
import * as NProgress from 'nprogress';

function getAuthor(state: GrievanceItemState): string {
    return state.anonymous ? (i18n.t('anonymous') as string) : `${state.firstName} ${state.lastName}`;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapFollowUps({ data }: any, state: GrievanceItemState, rootState: RootState): FollowUp {
    let author = rootState.customLabels?.grievanceFollowUpAuthor || '';
    if (data.type.id === 4) {
        author = getAuthor(state);
    } else if (data.meetingType?.value === 'TO_PORTAL') {
        author = rootState.customLabels?.instanceTitle || (i18n.t('anonymous') as string);
    }
    return {
        id: data.id.value,
        author,
        date: moment(data.date?.value.value.value).toDate(),
        description: data.description.value,
        documents: [],
        original: false,
    };
}
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function mapDocuments({ data, systemData }: any): Document {
    const fileName =
        data.fileName?.value && data.fileName?.suffix ? `${data.fileName?.value}${data.fileName?.suffix}` : '-';
    return {
        id: systemData.id,
        name: fileName,
        size: +data.fileSize?.value,
        type: systemData.file_icon_class,
    };
}

export const state: GrievanceItemState = {
    refNo: 'GR-00000',
    status: { text: '-', color: 'fff' },
    date: new Date(),
    type: null,
    parentCategory: null,
    category: null,
    description: '',
    anonymous: true,
    firstName: '',
    lastName: '',
    documents: [],
    followUps: [],
    labels: {},

    isLoading: false,
};

export const getters: GetterTree<GrievanceItemState, RootState> = {
    getFieldLabel: state => (field: string) => state.labels[field],
    getFollowUpById: state => (id: number) => state.followUps.find(c => c.id === id),
    getAuthor,
};

export const mutations: MutationTree<GrievanceItemState> = {
    setItemData(state, payload) {
        state.refNo = payload.refNo.value;
        state.status = { text: payload.status.formattedValue, color: payload.status.color };
        state.type = payload.typeString?.formattedValue;
        state.parentCategory = payload.parentCategoryString?.value;
        state.category = payload.categoryString?.value;
        state.description = payload.description.value;
        state.anonymous = payload.anonymous?.value;
        state.firstName = payload.firstName?.value;
        state.lastName = payload.lastName?.value;
        state.date = moment(payload.date?.value.value.value).toDate();
    },
    setLabels(state, payload) {
        state.labels = payload.labels;
    },
    setFollowUps(state, payload) {
        state.followUps = payload;
    },
    addFollowUp(state, payload) {
        state.followUps = [payload.followUp].concat(state.followUps);
    },
    setDocuments(state, payload) {
        state.documents = payload.documents;
    },
    setFollowUpDocuments(state, payload) {
        const followUpIndex = state.followUps.findIndex(followUp => followUp.id === payload.followUp.id);
        if (followUpIndex >= 0) {
            state.followUps.splice(followUpIndex, 1, { ...payload.followUp, documents: payload.documents });
        }
    },
    setIsLoading(state, payload) {
        state.isLoading = payload.isLoading;
        if (state.isLoading) {
            NProgress.start();
        } else {
            NProgress.done();
        }
    },
    resetItemData(state) {
        state.refNo = 'GR-00000';
        state.status = { text: '-', color: 'fff' };
        state.type = null;
        state.parentCategory = null;
        state.category = null;
        state.description = '';
        state.anonymous = false;
        state.firstName = '';
        state.lastName = '';
        state.date = new Date();
    },
};

export const actions: ActionTree<GrievanceItemState, RootState> = {
    async fetchData({ commit, rootState, state, dispatch }, payload) {
        if (!payload?.noLoading) {
            commit('setIsLoading', { isLoading: true });
        }
        if (!payload?.noReset) {
            commit('resetItemData');
        }
        try {
            const itemResponse = await axios.get(
                `/${rootState.instanceId}/portal_data/grievances/item/${rootState.session?.recordId}`,
            );
            commit('setItemData', itemResponse.data.data);
            commit('setLabels', { labels: itemResponse.data.metadata.labels });
            const documentsResponse = await axios.get(
                `/${rootState.instanceId}/portal_data/grievances/item/${rootState.session?.recordId}/relations/portal_documents`,
                {
                    params: { type: 'grid', limit: 50 },
                },
            );
            commit('setDocuments', { documents: documentsResponse.data.data.items.map(mapDocuments) });

            const followUpsResponse = await axios.get(
                `/${rootState.instanceId}/portal_data/grievances/item/${rootState.session?.recordId}/relations/followup_for_portal`,
                {
                    params: { type: 'grid', limit: 50 },
                },
            );
            commit(
                'setFollowUps',
                followUpsResponse.data.data.items.map((item: FollowUp) => mapFollowUps(item, state, rootState)),
            );

            const fetchFollowUpDocument = async (followUp: FollowUp) => {
                const followUpDocumentsResponse = await axios.get(
                    `/${rootState.instanceId}/portal_data/follow_ups/item/${followUp.id}/relations/portal_documents`,
                    {
                        params: { type: 'grid', limit: 50 },
                    },
                );
                commit('setFollowUpDocuments', {
                    followUp: followUp,
                    documents: followUpDocumentsResponse.data.data.items.map(mapDocuments),
                });
            };
            await Promise.all(state.followUps.map(fetchFollowUpDocument));
        } catch (error) {
            await dispatch('addError', { error }, { root: true });
            await dispatch('session/logOut', null, { root: true });
        } finally {
            await dispatch('session/setIsVerifying', { isVerifying: false }, { root: true });
            commit('setIsLoading', { isLoading: false });
        }
    },
};

const GrievanceItemStore: Module<GrievanceItemState, RootState> = {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
};

export default GrievanceItemStore;
